// Client-side NIP-05 helpers for T16. Companion to the serverless
// /api/nip05-verify proxy — we never fetch third-party domains from
// the browser (CSP is tight, CORS would be a mess anyway); this module
// just talks to our own proxy and interprets the result.
//
// parseNip05(string) — accepts the standard "name@domain.tld" form and
// the bare-domain shorthand, which NIP-05 treats as the root identifier
// (_@domain). Both parts are lowercased, consistent with the spec and
// with how the serverless proxy normalizes input.
//
// verifyNip05(nip05, expectedPubkey, { signal }) — calls the proxy and
// compares the returned pubkey to expectedPubkey (both lowercased).
// Returns a plain { verified: bool, reason?: string } object. Network
// faults are reported to Sentry (op=nip05_verify, extra=domain) and
// returned as verified: false — the caller treats verification failure
// as silent fallback to the self-claimed glyph, so this doesn't impact
// UX, just gives us visibility.

import * as Sentry from '@sentry/react'

export function parseNip05(input) {
  if (typeof input !== 'string') return null
  const trimmed = input.trim().toLowerCase()
  if (!trimmed) return null

  const parts = trimmed.split('@')
  if (parts.length === 1) {
    // Bare domain → root identifier.
    const domain = parts[0]
    if (!domain.includes('.')) return null
    return { name: '_', domain }
  }
  if (parts.length !== 2) return null

  const [name, domain] = parts
  if (!name || !domain || !domain.includes('.')) return null
  return { name, domain }
}

export async function verifyNip05(nip05, expectedPubkey, { signal } = {}) {
  const parsed = parseNip05(nip05)
  if (!parsed) return { verified: false, reason: 'malformed_nip05' }

  const url =
    `/api/nip05-verify?domain=${encodeURIComponent(parsed.domain)}` +
    `&name=${encodeURIComponent(parsed.name)}`

  try {
    const resp = await fetch(url, { signal })
    if (!resp.ok) return { verified: false, reason: 'proxy_error' }
    const data = await resp.json()
    if (!data?.ok) return { verified: false, reason: data?.reason || 'unknown' }
    const matches =
      typeof data.pubkey === 'string' &&
      typeof expectedPubkey === 'string' &&
      data.pubkey.toLowerCase() === expectedPubkey.toLowerCase()
    return { verified: matches, reason: matches ? null : 'pubkey_mismatch' }
  } catch (e) {
    if (e?.name === 'AbortError') return { verified: false, reason: 'aborted' }
    // Technical fault, not a user action — surface to Sentry so dead
    // proxies / network drops are visible. Domain only; the pubkey of
    // the user being checked is deliberately omitted per privacy posture.
    Sentry.withScope((scope) => {
      scope.setTag('op', 'nip05_verify')
      scope.setExtra('domain', parsed.domain)
      Sentry.captureException(e)
    })
    return { verified: false, reason: 'network_error' }
  }
}
